import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";

// Phases the backend reports, in the order they happen. The UI shows a sentence
// per phase rather than the phase name: "Interpreting" means nothing to someone
// who just asked for Opera.
export const PHASE_MESSAGES = {
  Pending: "Reading your request…",
  Interpreting: "Working out what you need…",
  Resolving: "Looking for a desktop that already has it…",
  WaitingForBake: "Someone else is already building this — waiting for it…",
  Baking: "Installing the software…",
  Snapshotting: "Saving the image…",
  Launching: "Starting your desktop…",
  Ready: "Your desktop is ready.",
  Failed: "That did not work."
};

export const TERMINAL_PHASES = ["Ready", "Failed"];

export const createDesktopRequest = createAsyncThunk(
  "api/desktop-requests/create",
  async ({ prompt, visibility, shouldPersist }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/desktop-requests`, {
        prompt,
        visibility,
        shouldPersist
      });
      return response.data;
    } catch (err) {
      // The backend sends a message chosen from a fixed table, keyed by a failure
      // code. Preferring it over err.message keeps internal detail off the screen.
      return rejectWithValue(err?.response?.data?.error || err?.response?.data?.message || null);
    }
  }
);

export const getDesktopRequest = createAsyncThunk(
  "api/desktop-requests/get",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/desktop-requests/${id}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.error || null);
    }
  }
);

// listDesktopRequests doubles as the availability probe. The backend only mounts
// these routes when PROMPT_DESKTOPS_ENABLED is on, so a 404 here means the feature
// is off for this deployment — which is better than a second copy of the flag in
// the frontend that can drift out of step with the backend's.
export const listDesktopRequests = createAsyncThunk(
  "api/desktop-requests/list",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/desktop-requests`);
      return response.data?.requests || [];
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) return rejectWithValue({ unavailable: true });
      return rejectWithValue({ unknown: true, message: err?.response?.data?.error || null });
    }
  }
);

const initialState = {
  // current is the request being watched, as the backend last reported it.
  current: null,
  history: [],
  submitting: false,
  error: null,
  // null until the probe answers, so nothing flashes on screen before we know
  // whether this deployment has the feature at all. It goes back to null on an
  // inconclusive failure, which is what lets the probe be retried.
  available: null,
  probeAttempts: 0
};

export const desktopRequestsSlice = createSlice({
  name: "desktopRequests",
  initialState,
  reducers: {
    clearDesktopRequest: (state) => {
      state.current = null;
      state.error = null;
      state.submitting = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createDesktopRequest.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(createDesktopRequest.fulfilled, (state, action) => {
        state.submitting = false;
        state.current = action.payload;
      })
      .addCase(createDesktopRequest.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload || null;
      })
      .addCase(listDesktopRequests.rejected, (state, action) => {
        state.probeAttempts += 1;
        if (action.payload?.unavailable) {
          state.available = false;
          return;
        }
        // Inconclusive. Leave it unknown so the panel can ask again, but stop after
        // a few tries rather than retrying a broken deployment for ever.
        state.available = state.probeAttempts >= 3 ? false : null;
      })
      .addCase(getDesktopRequest.fulfilled, (state, action) => {
        state.current = action.payload;
      })
      .addCase(listDesktopRequests.fulfilled, (state, action) => {
        state.history = action.payload;
        state.available = true;
        state.probeAttempts = 0;
      });
  }
});

export const { clearDesktopRequest } = desktopRequestsSlice.actions;
export default desktopRequestsSlice.reducer;
